/*
 * Copyright (c) 2020, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React from 'react';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';

import { makeStyles } from '@material-ui/core/styles';
import Rating from '@material-ui/lab/Rating';
import Box from '@material-ui/core/Box';

import * as Utils from '../../../data/utils';
import { postRating } from '../../../data/operations';
import { ApiReducer, RatingsReducer, ApiRating, ApplicationState, UserReducer } from '../../../data/interfaces';
import {trackAPIRatingChange} from "../../../util/app-insights";

const useStyles = makeStyles((theme) => ({
  root: {
    width: 225,
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),
  },
}));

interface ApiHoverRatingProps {
  apiReducer: ApiReducer,
  ratingsReducer: RatingsReducer,
  userReducer: UserReducer,
  postRating: (apiId: string, organizationId: string, rating: ApiRating) => Promise<void>,
}

function ApiHoverRating({ apiReducer, ratingsReducer, userReducer, postRating }: ApiHoverRatingProps) {
  const [value, setValue] = React.useState<number | null>(-1);
  const [hover, setHover] = React.useState(-1);
  const classes = useStyles();
  const intl = useIntl();
  const { api } = apiReducer;
  const { ratings } = ratingsReducer;
  const { orgs: userOrganizations, selectedOrg } = userReducer;

  const labels: { [index: string]: string } = {
    0: intl.formatMessage({ id: "api.apiOverview.apiHoverRating.ratingLabels.none", defaultMessage: "None" }),
    1: intl.formatMessage({ id: "api.apiOverview.apiHoverRating.ratingLabels.useless", defaultMessage: "Useless" }),
    2: intl.formatMessage({ id: "api.apiOverview.apiHoverRating.ratingLabels.poor", defaultMessage: "Poor" }),
    3: intl.formatMessage({ id: "api.apiOverview.apiHoverRating.ratingLabels.ok", defaultMessage: "Ok" }),
    4: intl.formatMessage({ id: "api.apiOverview.apiHoverRating.ratingLabels.good", defaultMessage: "Good" }),
    5: intl.formatMessage({ id: "api.apiOverview.apiHoverRating.ratingLabels.excellent", defaultMessage: "Excellent" }),
  };

  if (Utils.isValidData(ratings)) {
    if (value === -1) {
      setValue(ratings.userRating);
    }
  }

  const handleRatingChange = (event: any, value: number | null) => {
    setValue(value);
    const data: ApiRating = {
      rating: value || 0,
      ratingId: "",
      apiId: "",
      ratedBy: ""
    }

    if (api.id && selectedOrg.uuid) {
      postRating(api.id, selectedOrg.uuid, data);
      trackAPIRatingChange(api.id, `${value}`);
    }
  }

  return (
    <div className={classes.root}>
      <Rating
        data-testid="rating-stars"
        name="hover-feedback"
        value={value}
        precision={1}
        onChange={handleRatingChange}
        onChangeActive={(event, newHover) => {
          setHover(newHover);
        }}
      />
      {value !== null && <Box ml={2}>{labels[hover !== -1 ? hover : value]}</Box>}
    </div>
  );
}

const mapStateToProps = (state: ApplicationState) => {
  return {
    ratingsReducer: state.ratingsReducer,
    apiReducer: state.apiReducer,
    userReducer: state.userReducer
  }
}

const mapDispatchToProps = {
  postRating: postRating,
}

export default connect(mapStateToProps, mapDispatchToProps)(ApiHoverRating);
